<script>
import resize from '@utils/image-sizer';
import SetDetailsFull from '@components/circuit/set-details-full.vue';
import SetDetailsStacked from '@components/circuit/set-details-stacked.vue';
import { circuitModelMixin } from '@mixins/models/circuit-model-mixin.js';
import { breakpointsMixin } from '@mixins/breakpoints-mixin.js';
import { workoutComputed, workoutMethods, modalMethods } from '@state/helpers';

export default {
  mixins: [circuitModelMixin, breakpointsMixin],
  components: {
    SetDetailsFull,
    SetDetailsStacked,
  },
  props: {
    exercise: {
      type: Object,
      required: true,
      default: () => {},
    },
    exerciseIndex: {
      type: Number,
      required: true,
      default: null,
    },
    circuitIndex: {
      type: Number,
      required: true,
      default: null,
    },
    inPanel: {
      type: Boolean,
      required: false,
    },
    allowEditing: {
      type: Boolean,
      required: true,
      default: false,
    },
  },
  data() {
    return {
      placeholder: require('@assets/images/placeholder-photo-upload.svg'),
    };
  },
  computed: {
    ...workoutComputed,
    coverPhoto() {
      return this.exercise &&
        this.exercise.mediaAssets &&
        this.exercise.mediaAssets.coverPhoto &&
        this.exercise.mediaAssets.coverPhoto.url
        ? resize(this.exercise.mediaAssets.coverPhoto.url, 245, 138)
        : this.placeholder;
    },
    isLastExercise() {
      return (
        this.selectedWorkout &&
        this.selectedWorkout.circuits &&
        this.selectedWorkout.circuits.length &&
        this.selectedWorkout.circuits[this.circuitIndex].exercises &&
        this.selectedWorkout.circuits[this.circuitIndex].exercises.length &&
        this.exerciseIndex + 1 ===
          this.selectedWorkout.circuits[this.circuitIndex].exercises.length
      );
    },
    isSingleExerciseCircuit() {
      return (
        this.selectedWorkout &&
        this.selectedWorkout.circuits.length &&
        this.selectedWorkout.circuits[this.circuitIndex].exercises &&
        this.selectedWorkout.circuits[this.circuitIndex].exercises.length === 1
      );
    },
  },
  mounted() {},
  methods: {
    ...workoutMethods,
    ...modalMethods,
    onUpdateSets(type) {
      let newSet = null;
      if (
        type === 'add' &&
        this.exercise &&
        this.exercise.setDirections.length > 0
      ) {
        newSet = this.exercise.setDirections[
          this.exercise.setDirections.length - 1
        ];
      } else {
        newSet = this.getCircuitExerciseSetModel();
      }
      this.setUpdateWorkoutExerciseSet({
        set: newSet,
        circuitIndex: this.circuitIndex,
        exerciseIndex: this.exerciseIndex,
        type,
      });
    },
    onAdjustReps(exerciseSetRep) {
      this.setUpdateWorkoutExerciseSetReps({
        ...this.getCircuitExerciseSetModel(),
        circuitIndex: this.circuitIndex,
        exerciseIndex: this.exerciseIndex,
        exerciseSetIndex: exerciseSetRep.index,
        count: exerciseSetRep.count,
      });
    },
    onAdjustTime(exerciseSetTime) {
      this.setUpdateWorkoutExerciseSetTime({
        ...this.getCircuitExerciseSetModel(),
        circuitIndex: this.circuitIndex,
        exerciseIndex: this.exerciseIndex,
        exerciseSetIndex: exerciseSetTime.index,
        count: exerciseSetTime.count,
      });
    },
    onAdjustWeight(exerciseSetWeight) {
      this.setUpdateWorkoutExerciseSetWeight({
        ...this.getCircuitExerciseSetModel(),
        circuitIndex: this.circuitIndex,
        exerciseIndex: this.exerciseIndex,
        exerciseSetIndex: exerciseSetWeight.index,
        count: exerciseSetWeight.count,
      });
    },
    onAdjustRest(exerciseSetRest) {
      this.setUpdateWorkoutExerciseSetRest({
        ...this.getCircuitExerciseSetModel(),
        circuitIndex: this.circuitIndex,
        exerciseIndex: this.exerciseIndex,
        exerciseSetIndex: exerciseSetRest.index,
        count: exerciseSetRest.count,
      });
    },
    removeWorkoutCircuitExercise() {
      this.setRemoveWorkoutCircuitExercise({
        set: this.getCircuitExerciseSetModel(),
        circuitIndex: this.circuitIndex,
        exerciseIndex: this.exerciseIndex,
      });
    },
    showExerciseList() {
      this.$emit('show-exercise-list');
    },
    onViewPhoto(src) {
      this.setSelectedImage({ src });
      this.$bvModal.show('fullsize-image-modal');
    },
    moveWorkoutExerciseUp() {
      if (this.exerciseIndex > 0) {
        this.setMoveWorkoutExerciseUp({
          circuitIndex: this.circuitIndex,
          exerciseIndex: this.exerciseIndex,
        });
      }
    },
    moveWorkoutExerciseDown() {
      if (
        this.exerciseIndex <
        this.selectedWorkout.circuits[this.circuitIndex].exercises.length - 1
      ) {
        this.setMoveWorkoutExerciseDown({
          circuitIndex: this.circuitIndex,
          exerciseIndex: this.exerciseIndex,
        });
      }
    },
  },
};
</script>

<template>
  <div :class="['exercise-set-details', { inPanel: inPanel }]">
    <SetDetailsStacked
      v-if="inPanel || !isXLTabletUp"
      :exercise="exercise"
      :coverPhoto="coverPhoto"
      :label="exercise.label"
      :count="exercise.setDirections.length"
      :isLastExercise="isLastExercise"
      :isSingleExerciseCircuit="isSingleExerciseCircuit"
      @view-photo="onViewPhoto(coverPhoto)"
      @update-sets="onUpdateSets"
      @adjust-reps="onAdjustReps"
      @adjust-time="onAdjustTime"
      @adjust-weight="onAdjustWeight"
      @adjust-rest="onAdjustRest"
      @show-exercise-list="showExerciseList"
      @remove-exercise="removeWorkoutCircuitExercise"
      @move-up="moveWorkoutExerciseUp"
      @move-down="moveWorkoutExerciseDown"
      :allowEditing="allowEditing"
    />

    <SetDetailsFull
      v-if="!inPanel && isXLTabletUp"
      :exercise="exercise"
      :coverPhoto="coverPhoto"
      :label="exercise.label"
      :count="exercise.setDirections.length"
      :isLastExercise="isLastExercise"
      :isSingleExerciseCircuit="isSingleExerciseCircuit"
      @view-photo="onViewPhoto(coverPhoto)"
      @update-sets="onUpdateSets"
      @adjust-reps="onAdjustReps"
      @adjust-time="onAdjustTime"
      @adjust-weight="onAdjustWeight"
      @adjust-rest="onAdjustRest"
      @show-exercise-list="showExerciseList"
      @remove-exercise="removeWorkoutCircuitExercise"
      @move-up="moveWorkoutExerciseUp"
      @move-down="moveWorkoutExerciseDown"
      :allowEditing="allowEditing"
    />
  </div>
</template>

<style lang="scss" scoped>
@import '@design';
// stylelint-disable no-descending-specificity
// stylelint-disable selector-pseudo-element-no-unknown

.exercise-set-details {
  padding-bottom: 1.5rem;
  padding-top: 2.5rem;
  transition: all 300ms;

  &:not(:first-child) {
    border-top: 6px solid $color-light-gray-ten;
  }
}
</style>
